import { type CSSProperties, type PropsWithChildren, useEffect, useRef, useState } from 'react';
import cn from 'clsx';
import { useResize } from '@ev-tech/ui-core-components/utils';

import styles from './ExpandableLabel.module.css';

const INTERACTIVE_CONTENT_SELECTOR = 'a, button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])';

export interface ExpandableLabelProps {
  readLessLabel?: string;
  readMoreLabel?: string;
  maxLines?: number;
  isRequired?: boolean;
  className?: string;
  'data-testid'?: string;
}

export const ExpandableLabel = ({
  readLessLabel,
  readMoreLabel,
  maxLines,
  isRequired,
  className,
  children,
  'data-testid': dataTestId,
}: PropsWithChildren<ExpandableLabelProps>) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentDoesOverflow, setContentDoesOverflow] = useState(false);
  const [hasInteractiveDescendants, setHasInteractiveDescendants] = useState(false);
  const contentRef = useRef<HTMLSpanElement>(null);
  const width = useResize();

  function handleClick() {
    setIsExpanded(!isExpanded);
  }

  function handleLabelFocus() {
    if (!maxLines || isExpanded) return;

    setIsExpanded(true);
  }

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    setContentDoesOverflow(() => {
      return isExpanded || contentElement.scrollHeight - contentElement.clientHeight > 1;
    });
    setHasInteractiveDescendants(Boolean(contentElement.querySelector(INTERACTIVE_CONTENT_SELECTOR)));
  }, [children, maxLines, width, isExpanded]);

  const isLabelTabbable = Boolean(maxLines && hasInteractiveDescendants);
  const isClamped = Boolean(maxLines) && !isExpanded;

  const label = (
    <span
      className={styles.label}
      data-required={isRequired}
      data-clamped={isClamped}
      style={maxLines ? ({ '--max-lines': maxLines } as CSSProperties) : undefined}
      ref={contentRef}
      onFocusCapture={handleLabelFocus}
      data-testid={dataTestId ? `${dataTestId}_content` : undefined}
      tabIndex={isLabelTabbable ? 0 : undefined}
    >
      {children}
    </span>
  );

  return maxLines ? (
    <div className={cn(styles.wrapper, className)} data-testid={dataTestId}>
      {label}
      {contentDoesOverflow && (
        <button
          className={styles.button}
          onClick={handleClick}
          data-required={isRequired}
          type="button"
          data-testid={`${dataTestId}_button`}
        >
          {isExpanded ? readLessLabel : readMoreLabel}
        </button>
      )}
    </div>
  ) : (
    label
  );
};
